import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Contract } from "@models/contract.model";
import { Budget } from "@models/budget.model";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { SpendAccount } from "@models/spendaccount.model";
import { Credit } from "@models/credit.model";
import { Debit } from "@models/debit.model";
import { CreateContractPayload } from "@services/finance/contracts/commands/cmdCreateContract";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import { CreateBudgetPayload } from "@services/finance/budgets/commands/cmdCreateBudget";
import { UpdateBudgetPayload } from "@services/finance/budgets/commands/cmdUpdateBudget";
import { CreatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdCreatePricesheet";
import { UpdatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdUpdatePricesheet";
import { CreateCreditPayload } from "@services/finance/credits/commands/cmdCreateCredit";
import { UpdateCreditPayload } from "@services/finance/credits/commands/cmdUpdateCredit";
import { CreateDebitPayload } from "@services/finance/debits/commands/cmdCreateDebit";
import { UpdateDebitPayload } from "@services/finance/debits/commands/cmdUpdateDebit";
import AggregateVersionsDrawer from "@/shared/components/versions/AggregateVersionsDrawer";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import { AggregateVersion } from "@models/aggregateVersion.model";
import { ContractService } from "@services/finance/contracts/contractService";
import { CONTRACT_TRIGGER_LABELS } from "@services/finance/contracts/commands/cmdListContractVersions";
import ModalDelete from "src/components/common/ModalDelete";
import ContractDetailCard from "./ContractDetailCard";
import ContractFormModal from "./modals/ContractFormModal";
import BudgetFormModal from "./modals/BudgetFormModal";
import PricesheetFormModal, { ItemChanges, PathChanges } from "./modals/PricesheetFormModal";
import CreditFormModal from "./modals/CreditFormModal";
import DebitFormModal from "./modals/DebitFormModal";

/** Every write the editor can perform. Supplied by whichever hook owns the data. */
export interface ContractMutations {
  updateContract: (contractId: number, payload: UpdateContractPayload) => void | Promise<void>;
  deleteContract: (contractId: number) => void | Promise<void>;
  createBudget: (payload: CreateBudgetPayload) => void | Promise<void>;
  updateBudget: (budgetId: number, payload: UpdateBudgetPayload) => void | Promise<void>;
  deleteBudget: (budgetId: number) => void | Promise<void>;
  savePricesheet: (
    pricesheetId: number | null,
    payload: CreatePricesheetPayload | UpdatePricesheetPayload,
    itemChanges: ItemChanges,
    pathChanges: PathChanges,
  ) => void | Promise<void>;
  deletePricesheet: (pricesheetId: number) => void | Promise<void>;
  createCredit: (payload: CreateCreditPayload) => void | Promise<void>;
  updateCredit: (creditId: number, payload: UpdateCreditPayload) => void | Promise<void>;
  deleteCredit: (creditId: number) => void | Promise<void>;
  createDebit: (payload: CreateDebitPayload) => void | Promise<void>;
  updateDebit: (debitId: number, payload: UpdateDebitPayload) => void | Promise<void>;
  deleteDebit: (debitId: number) => void | Promise<void>;
}

type DeleteTarget =
  | { kind: "contract"; contract: Contract }
  | { kind: "budget"; budget: Budget }
  | { kind: "pricesheet"; pricesheet: Pricesheet }
  | { kind: "credit"; credit: Credit }
  | { kind: "debit"; debit: Debit };

interface Props {
  contract: Contract;
  budgets: readonly Budget[];
  pricesheets: readonly Pricesheet[];
  pricesheetItems: readonly PricesheetItem[];
  spendAccount?: SpendAccount | null;
  credits?: readonly Credit[];
  debits?: readonly Debit[];
  balance?: number | null;
  /** Client org the contract belongs to — needed by the contract/pricesheet forms. */
  organizationId: number;
  isSaving?: boolean;
  title?: string;
  statusSlot?: ReactNode;
  actionsSlot?: ReactNode;
  mutations: ContractMutations;
  /** Pricesheet editing is a full page, so the host owns that navigation. */
  onEditPricesheet: (pricesheet: Pricesheet) => void;
  /** Fired after the contract itself is deleted. */
  onContractDeleted?: () => void;
}

/*
    The contract editing surface: detail card plus every form it can open.
    Shared by the organization contracts tab and the engagement Contract item
    so both write through the same flows.
*/
const ContractEditor: FC<Props> = ({
  contract,
  budgets,
  pricesheets,
  pricesheetItems,
  spendAccount = null,
  credits = [],
  debits = [],
  balance = null,
  organizationId,
  isSaving = false,
  title,
  statusSlot,
  actionsSlot,
  mutations,
  onEditPricesheet,
  onContractDeleted,
}) => {
  const [contractModal, setContractModal] = useState(false);
  const [budgetModal, setBudgetModal] = useState<{ budget: Budget | null } | null>(null);
  const [pricesheetModal, setPricesheetModal] = useState<{ pricesheet: Pricesheet | null } | null>(
    null,
  );
  const [creditModal, setCreditModal] = useState<{ credit: Credit | null } | null>(null);
  const [debitModal, setDebitModal] = useState<{ debit: Debit | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSaveContract = async (payload: CreateContractPayload | UpdateContractPayload) => {
    await mutations.updateContract(contract.id, payload as UpdateContractPayload);
    setContractModal(false);
  };

  const handleSaveBudget = async (payload: CreateBudgetPayload | UpdateBudgetPayload) => {
    if (budgetModal?.budget) {
      await mutations.updateBudget(budgetModal.budget.id, payload as UpdateBudgetPayload);
    } else {
      await mutations.createBudget(payload as CreateBudgetPayload);
    }
    setBudgetModal(null);
  };

  const handleSavePricesheet = async (
    payload: CreatePricesheetPayload | UpdatePricesheetPayload,
    itemChanges: ItemChanges,
    pathChanges: PathChanges,
  ) => {
    await mutations.savePricesheet(
      pricesheetModal?.pricesheet?.id ?? null,
      payload,
      itemChanges,
      pathChanges,
    );
    setPricesheetModal(null);
  };

  const handleSaveCredit = async (payload: CreateCreditPayload | UpdateCreditPayload) => {
    if (creditModal?.credit) {
      await mutations.updateCredit(creditModal.credit.id, payload as UpdateCreditPayload);
    } else {
      await mutations.createCredit(payload as CreateCreditPayload);
    }
    setCreditModal(null);
  };

  const handleSaveDebit = async (payload: CreateDebitPayload | UpdateDebitPayload) => {
    if (debitModal?.debit) {
      await mutations.updateDebit(debitModal.debit.id, payload as UpdateDebitPayload);
    } else {
      await mutations.createDebit(payload as CreateDebitPayload);
    }
    setDebitModal(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      if (deleteTarget.kind === "contract") {
        await mutations.deleteContract(deleteTarget.contract.id);
        onContractDeleted?.();
      } else if (deleteTarget.kind === "budget") {
        await mutations.deleteBudget(deleteTarget.budget.id);
      } else if (deleteTarget.kind === "pricesheet") {
        await mutations.deletePricesheet(deleteTarget.pricesheet.id);
      } else if (deleteTarget.kind === "credit") {
        await mutations.deleteCredit(deleteTarget.credit.id);
      } else {
        await mutations.deleteDebit(deleteTarget.debit.id);
      }
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const deleteLabel = !deleteTarget
    ? ""
    : deleteTarget.kind === "contract"
      ? title ?? `Contract #${deleteTarget.contract.id}`
      : deleteTarget.kind === "budget"
        ? `${deleteTarget.budget.start_date} budget`
        : deleteTarget.kind === "pricesheet"
          ? `Pricesheet #${deleteTarget.pricesheet.id}`
          : deleteTarget.kind === "credit"
            ? `credit of ${deleteTarget.credit.amount}`
            : `debit of ${deleteTarget.debit.amount}`;

  // Term history (MPN-1336). Fetched when the drawer opens rather than alongside the page: it is
  // only ever read deliberately, and most visits never open it.
  const contractSvc = useMemo(() => new ContractService(), []);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<AggregateVersion[] | null>(null);
  const [historyError, setHistoryError] = useState(false);

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    // Always refetch on open. Several of the modals below write through injected mutations that
    // raise no event this component listens for, so cached rows would show the history as it
    // stood before the edit the user just made.
    setVersions(null);
    setHistoryError(false);
    try {
      setVersions(await contractSvc.listVersions(contract.id));
    } catch {
      setVersions([]);
      setHistoryError(true);
    }
  }, [contractSvc, contract.id]);

  // Drop another contract's history rather than showing it under this one's name.
  useEffect(() => {
    setVersions(null);
  }, [contract.id]);

  // Composed, not replaced: `actionsSlot` is supplied by callers (engagementContractTab passes
  // its own), so History is added beside whatever they gave us.
  const headerActions = (
    <>
      <HistoryButton onClick={openHistory} title="Contract term history" />
      {actionsSlot}
    </>
  );

  return (
    <>
      <ContractDetailCard
        contract={contract}
        budgets={budgets}
        pricesheets={pricesheets}
        pricesheetItems={pricesheetItems}
        spendAccount={spendAccount}
        credits={credits}
        debits={debits}
        balance={balance}
        title={title}
        statusSlot={statusSlot}
        actionsSlot={headerActions}
        onEditContract={() => setContractModal(true)}
        onDeleteContract={(c) => setDeleteTarget({ kind: "contract", contract: c })}
        onUpdateContract={(payload) => mutations.updateContract(contract.id, payload)}
        onCreateBudget={() => setBudgetModal({ budget: null })}
        onEditBudget={(b) => setBudgetModal({ budget: b })}
        onDeleteBudget={(b) => setDeleteTarget({ kind: "budget", budget: b })}
        onCreatePricesheet={() => setPricesheetModal({ pricesheet: null })}
        onEditPricesheet={onEditPricesheet}
        onDeletePricesheet={(p) => setDeleteTarget({ kind: "pricesheet", pricesheet: p })}
        onAddCredit={() => setCreditModal({ credit: null })}
        onEditCredit={(c) => setCreditModal({ credit: c })}
        onDeleteCredit={(c) => setDeleteTarget({ kind: "credit", credit: c })}
        onAddDebit={() => setDebitModal({ debit: null })}
        onEditDebit={(d) => setDebitModal({ debit: d })}
        onDeleteDebit={(d) => setDeleteTarget({ kind: "debit", debit: d })}
      />

      {contractModal && (
        <ContractFormModal
          show
          onClose={() => setContractModal(false)}
          onSave={handleSaveContract}
          contract={contract}
          clientOrgId={organizationId}
          isSaving={isSaving}
        />
      )}

      {budgetModal && (
        <BudgetFormModal
          show
          onClose={() => setBudgetModal(null)}
          onSave={handleSaveBudget}
          budget={budgetModal.budget}
          contractId={contract.id}
          contractBillingMethod={contract.billing_method ?? null}
          isSaving={isSaving}
        />
      )}

      {pricesheetModal && (
        <PricesheetFormModal
          show
          onClose={() => setPricesheetModal(null)}
          onSave={handleSavePricesheet}
          pricesheet={pricesheetModal.pricesheet}
          isSaving={isSaving}
          pricesheetItems={pricesheetItems}
          organizationId={organizationId}
          contractId={contract.id}
          journeyId={contract.journey_id ?? null}
        />
      )}

      {creditModal && (
        <CreditFormModal
          show
          onClose={() => setCreditModal(null)}
          onSave={handleSaveCredit}
          credit={creditModal.credit}
          budgets={budgets}
          isSaving={isSaving}
        />
      )}

      {debitModal && (
        <DebitFormModal
          show
          onClose={() => setDebitModal(null)}
          onSave={handleSaveDebit}
          debit={debitModal.debit}
          isSaving={isSaving}
        />
      )}

      <ModalDelete
        title={deleteTarget?.kind ?? "Item"}
        objectLabel={deleteLabel}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isDeleting}
      />
      <AggregateVersionsDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={versions ?? []}
        title="Contract history"
        subtitle={title ?? `Contract #${contract.id}`}
        emptyMessage={
          historyError
            ? "Could not load the contract history."
            : versions === null
              ? "Loading…"
              : "No history yet. Editing the contract creates the first version."
        }
        triggerLabels={CONTRACT_TRIGGER_LABELS}
      />
    </>
  );
};

export default ContractEditor;
